import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import GlobalSearch from './GlobalSearch';
import HeaderUserMenu from './HeaderUserMenu';

const ModernHeader = ({ setSidebarOpen, user, title, globalSearchRef, logout }) => {
  const isProduction = process.env.NODE_ENV === 'production';
  const [currentDateTime, setCurrentDateTime] = useState('');

  useEffect(() => {
    // PROMPT-058-FIX-01: Update date & time every second in 12-hour AM/PM format
    const updateDateTime = () => {
      const now = new Date();
      const day = String(now.getDate()).padStart(2, '0');
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const year = now.getFullYear();
      
      let hours = now.getHours();
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      
      // Convert to 12-hour format
      hours = hours % 12;
      hours = hours ? hours : 12; // 0 should be 12
      const formattedHours = String(hours).padStart(2, '0');
      
      setCurrentDateTime(`${day}-${month}-${year}  ${formattedHours}:${minutes}:${seconds} ${ampm}`);
    };

    updateDateTime(); // Initial update
    const interval = setInterval(updateDateTime, 1000);

    return () => clearInterval(interval); // Cleanup on unmount
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 w-full" style={{
      background: 'rgba(255, 255, 255, 0.88)',
      backdropFilter: 'blur(24px) saturate(180%)',
      WebkitBackdropFilter: 'blur(24px) saturate(180%)',
      borderBottom: '1px solid rgba(99, 102, 241, 0.06)',
      boxShadow: '0 1px 3px rgba(99, 102, 241, 0.04), 0 4px 16px rgba(99, 102, 241, 0.02)',
    }}>
      {/* Top accent gradient line */}
      <div className="absolute top-0 left-0 right-0 h-[2px]" style={{
        background: 'linear-gradient(90deg, #4f46e5 0%, #06b6d4 40%, #8b5cf6 70%, #06b6d4 100%)',
      }} />

      <div className="w-full px-4 sm:px-5 lg:px-6">
        <div className="flex items-center justify-between" style={{ height: '62px' }}>
          {/* Left section */}
          <div className="flex items-center gap-3">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden rounded-lg text-gray-500 hover:text-indigo-600 hover:bg-indigo-50/80 transition-all duration-150"
              style={{ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <Icons.Menu className="w-[18px] h-[18px]" />
            </button>

            {/* Logo - Always visible, clickable to dashboard */}
            <Link to="/" className="flex items-center gap-2.5 group">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 group-hover:scale-105" style={{
                background: 'linear-gradient(135deg, #4f46e5 0%, #06b6d4 100%)',
                boxShadow: '0 2px 8px rgba(79, 70, 229, 0.25)',
              }}>
                <Icons.Zap className="w-4 h-4 text-white" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-[15px] font-bold text-gray-900 tracking-tight leading-tight">Way2Connect</h1>
                <p className="text-[9px] font-semibold tracking-[0.15em] uppercase leading-tight hidden lg:block" style={{ color: '#818cf8' }}>SMPP Platform</p>
              </div>
            </Link>

            {/* Page title (desktop) */}
            {title && (
              <div className="hidden lg:flex items-center pl-3.5 ml-1" style={{ borderLeft: '1.5px solid rgba(99, 102, 241, 0.12)' }}>
                <h1 className="text-sm font-semibold text-gray-600">{title}</h1>
              </div>
            )}
          </div>

          {/* Right section */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* Global Search - Master Admin Only */}
            {user?.role === 'master_admin' && (
              <GlobalSearch ref={globalSearchRef} />
            )}
            
            {/* Live Date & Time */}
            <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg transition-all duration-150" style={{
              background: 'rgba(99, 102, 241, 0.04)',
              border: '1px solid rgba(99, 102, 241, 0.08)',
            }}>
              <Icons.Clock className="w-3 h-3" style={{ color: '#818cf8' }} />
              <span className="text-[11px] font-mono font-medium text-gray-500 tabular-nums">
                {currentDateTime}
              </span>
            </div>

            {/* Environment Badge */}
            <div className="hidden sm:flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold tracking-wide transition-all duration-150" style={isProduction ? {
              background: 'rgba(16, 185, 129, 0.08)',
              border: '1px solid rgba(16, 185, 129, 0.12)',
              color: '#059669',
            } : {
              background: 'rgba(245, 158, 11, 0.08)',
              border: '1px solid rgba(245, 158, 11, 0.15)',
              color: '#d97706',
            }}>
              <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{
                background: isProduction ? '#10b981' : '#f59e0b',
              }} />
              {isProduction ? 'LIVE' : 'TEST'}
            </div>

            {/* Notifications */}
            <button className="relative rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50/80 transition-all duration-150" style={{ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icons.Bell className="w-[18px] h-[18px]" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full" style={{
                background: 'linear-gradient(135deg, #ef4444, #f97316)',
                boxShadow: '0 0 0 2px rgba(255,255,255,0.9)',
              }}>
                <span className="absolute inset-0 rounded-full animate-ping opacity-40" style={{ background: '#ef4444' }} />
              </span>
            </button>

            {/* User menu */}
            <HeaderUserMenu user={user} logout={logout} />
          </div>
        </div>
      </div>
    </header>
  );
};

export default ModernHeader;
